/** 
 * UI component for Admin Users screen. It:
 * - renders screen "Admin Users"
 * - has different views for mobile and desktop
 * - renders list of users with pet names and directory usage counts
 * - has search by user email and pet name
 * - has "Load more" button for pagination
 * - has "Update" button to reload the list
 * - supports bulk selection and delete
 * - shows loading and error states
 */
//react hooks
import { useMemo, useState } from "react";
import { useMediaQuery } from "react-responsive";

//styles and images
import { BackScreenDiv, Button, ClearBtn, ContentDiv, Div, H1, HeaderDiv, Image, Input, P, ScreenDiv, SearchWrap, TableColumnDiv, UsersActivityCell, UsersActivityHeaderCell, UsersActivityLatest, UsersHeaderCell, UsersTableFrame, UsersTableHeaderDiv, UsersTableRowDiv, UsersTableScroll, UserColumnRowDiv, UserTableBodyDiv, UserTableRowDiv } from "../../../styles/styles";
import { USER_ACTIVITY_COLUMNS, getTimestampValue } from "./adminUserActivity";
import closeButton from '../../../images/close-icon.svg';

//firebase
import { Timestamp } from "firebase/firestore";

//dayjs
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

//components
import CustomModal from "../../shared/customModal/customModal";

//custom hooks
import { useLogoutModal } from "../../../hooks/useLogoutModal";

dayjs.extend(customParseFormat);


const formatDate = (ts) => {
  try {
    let dateObj = null;
    
    if (ts instanceof Timestamp) {
      dateObj = ts.toDate();
    } else if (ts?.toDate) {
      dateObj = ts.toDate();
    } else if (ts instanceof Date) {
      dateObj = ts;
    } else if (typeof ts === "string") {
      // Try parsing as ISO date string first
      const d = new Date(ts);
      if (!isNaN(d.getTime())) {
        dateObj = d;
      } else {
        // Try parsing as DD.MM.YYYY format
        const parsed = dayjs(ts, "DD.MM.YYYY");
        if (parsed.isValid()) {
          dateObj = parsed.toDate();
        }
      }
    }
    
    if (dateObj) {
      return dateObj.toLocaleDateString();
    }
  } catch (e) {
    console.error("Error formatting date:", e);
  }
  return "—";
};

const PET_TYPE_LABELS = {
  dog: "Dog",
  cat: "Cat",
  rabbit: "Rabbit",
  parrot: "Parrot",
  hamster: "Hamster",
  guinea_pig: "Guinea pig",
  ferret: "Ferret",
  rat: "Rat",
  turtle: "Turtle",
  fish: "Fish",
  other: "Other",
};

const formatPetLabel = (pet) => {
  const name = pet?.name || "Unnamed";
  const rawType = typeof pet?.type === "string" ? pet.type.trim() : "";
  const custom = typeof pet?.customTypeName === "string" ? pet.customTypeName.trim() : "";
  const typeLabel = rawType === "other" && custom
    ? custom
    : PET_TYPE_LABELS[rawType] || rawType;
  return typeLabel ? `${typeLabel} ${name}` : name;
};

const PLATFORM_LABELS = {
  ios: "Apple",
  android: "Google",
  web: "Web",
};

const getPlatformLabel = (platform) => {
  if (!platform || typeof platform !== "string") return "—";
  return PLATFORM_LABELS[platform] || platform;
};

const isPremiumUser = (user) => {
  const source = typeof user?.premiumSource === "string"
    ? user.premiumSource.trim().toLowerCase()
    : "";
  const expires = getTimestampValue(user?.premiumExpiresAt);
  const expiresActive = !expires || expires.getTime() > Date.now();
  const legacy = user?.accessTier == null;
  const tierRaw = legacy ? user?.plan : user?.accessTier;
  const tier = typeof tierRaw === "string" ? tierRaw.trim().toLowerCase() : "free";
  const isGrant = source === "lifetime" || source === "shelter" || source === "manual" || source === "promo";
  return expiresActive && (tier === "premium" || (isGrant && source !== "subscription"));
};

const BILLING_PERIOD_LABELS = {
  month: "month",
  six_months: "6 mo",
  year: "year",
};

/**
 * Under the star: subscription period (+ trial / intro from RevenueCat),
 * or where a granted Premium comes from (family, manual, promo…).
 */
const getPlanDetails = (user) => {
  const source = typeof user?.premiumSource === "string"
    ? user.premiumSource.trim().toLowerCase()
    : "";
  if (source && source !== "subscription") return { label: source, note: "" };
  const period = BILLING_PERIOD_LABELS[user?.subscriptionBillingPeriod] || "";
  const periodType = typeof user?.subscriptionPeriodType === "string"
    ? user.subscriptionPeriodType
    : "";
  const note = periodType === "trial" || periodType === "intro" ? periodType : "";
  return { label: period, note };
};

function PlanMark({ user }) {
  if (!isPremiumUser(user)) {
    return "free";
  }
  const source = typeof user?.premiumSource === "string" ? user.premiumSource : "premium";
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      aria-label="Premium"
      role="img"
      style={{ display: "block" }}
    >
      <title>{source}</title>
      <path
        d="M12 3.1l2.35 4.76 5.25.76-3.8 3.7.9 5.23L12 15.1l-4.7 2.45.9-5.23-3.8-3.7 5.25-.76L12 3.1z"
        fill="none"
        stroke="#202020"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// The cell is a <p>: lines are block spans, not divs.
const FAMILY_LINE_STYLE = {
  display: "block",
  fontSize: 11,
  lineHeight: "14px",
  color: "#00897b",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

/** Under the email: owner of a member, or members of an owner. */
function FamilyLines({ user }) {
  if (user?.familyOwnerEmail) {
    return (
      <span style={FAMILY_LINE_STYLE} title={`Family member of ${user.familyOwnerEmail}`}>
        ↳ member of {user.familyOwnerEmail}
      </span>
    );
  }
  const members = user?.familyMemberEmails || [];
  return members.map((email) => (
    <span key={email} style={FAMILY_LINE_STYLE} title={`Family member: ${email}`}>
      ↳ family: {email}
    </span>
  ));
}

const getSignupSourceLabel = (user) => getPlatformLabel(user?.signupPlatform);

const getSignupCountryLabel = (user) => {
  const country = user?.signupCountry;
  if (!country || typeof country !== "string") return "—";
  const trimmed = country.trim().toUpperCase();
  return trimmed || "—";
};

const getLastAppLabel = (user) => {
  const platform = user?.lastLoginPlatform;
  if (!platform || typeof platform !== "string") {
    return { label: "—", title: "No app session recorded" };
  }
  const version =
    typeof user.appVersion === "string" && user.appVersion ? user.appVersion : "";
  const build =
    typeof user.appBuild === "string" && user.appBuild ? user.appBuild : "";
  // Apps before 1.0.11 do not report a version.
  const label = version
    ? `${getPlatformLabel(platform)} ${version}`
    : getPlatformLabel(platform);
  const openedAt = formatDate(user.lastLoginAt);
  const others = Array.isArray(user.loginPlatforms)
    ? user.loginPlatforms
        .filter((item) => typeof item === "string" && item && item !== platform)
        .map((item) => getPlatformLabel(item))
    : [];
  const parts = [];
  parts.push(
    version
      ? `App ${version}${build ? ` (build ${build})` : ""}`
      : "App 1.0.10 or older (no version reported)",
  );
  if (openedAt && openedAt !== "—") parts.push(`Last open: ${openedAt}`);
  if (others.length) parts.push(`Also opened: ${others.join(", ")}`);
  return { label, title: parts.join(". ") || label };
};

const getPushLabel = (user) => {
  const tokens = Array.isArray(user?.pushTokens)
    ? user.pushTokens.filter((token) => typeof token === "string" && token.trim())
    : [];
  const turnedOff = user?.notificationsSettings?.pushEnabled === false;
  if (tokens.length > 0 && !turnedOff) {
    return {
      label: "Yes",
      title: `Push on, ${tokens.length} device${tokens.length === 1 ? "" : "s"}`,
    };
  }
  if (turnedOff) {
    return {
      label: "Off",
      title: tokens.length
        ? "Push turned off in the app. A device token is still saved."
        : "Push turned off in the app",
    };
  }
  return { label: "No", title: "No device push token" };
};

const headerSortButtonStyle = {
  background: "none",
  border: 0,
  padding: 0,
  margin: 0,
  font: "inherit",
  color: "inherit",
  cursor: "pointer",
  textAlign: "inherit",
};

function sortMark(key, sortKey, sortDir) {
  if (key !== sortKey) return "";
  return sortDir === "asc" ? " ↑" : " ↓";
}

const getArticlesReadCount = (user) => {
  const raw = user?.readArticles;
  if (!Array.isArray(raw)) return 0;
  return raw.filter((id) => typeof id === "string" && id.trim()).length;
};

const getPaywallLabel = (user) => {
  const count = typeof user?.paywallViewCount === "number" ? user.paywallViewCount : 0;
  if (!count) return { count: 0, title: "Paywall not opened yet" };
  const at = formatDate(user.lastPaywallViewAt);
  const source = typeof user.lastPaywallSource === "string" ? user.lastPaywallSource : "";
  const parts = [`Opened ${count} time${count === 1 ? "" : "s"}`];
  if (at && at !== "—") parts.push(at);
  if (source) parts.push(source);
  return { count, title: parts.join(" · ") };
};

export default function AdminUsers({
  rows,
  totalUsers = 0,
  loading,
  error,
  done,
  search,
  onSearchChange,
  sortKey = "lastActivity",
  sortDir = "desc",
  onSort,
  onRefresh,
  onLoadMore,
  selectedIds,
  selectedUsers = [],
  currentUserId,
  deleting,
  onToggleSelect,
  onToggleSelectAllVisible,
  onClearSelection,
  onDeleteSelected,
}) {
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const handleKeyDown = (e) => {
    if (e.key === "Escape" && search) {
      onSearchChange("");
      e.stopPropagation();
      e.preventDefault();
    }
  };

  const isDesktop = useMediaQuery({ minWidth: '800px' });

  const { open, openModal, closeModal, confirmLogout } = useLogoutModal();

  const selectedCount = selectedIds?.size || 0;

  const selectableVisibleIds = useMemo(
    () => rows.map((u) => u.id).filter((id) => id && id !== currentUserId),
    [rows, currentUserId],
  );

  const allVisibleSelected =
    selectableVisibleIds.length > 0 &&
    selectableVisibleIds.every((id) => selectedIds.has(id));

  const selectedEmailsPreview = useMemo(
    () => selectedUsers.map((u) => u.email || u.id).slice(0, 10),
    [selectedUsers],
  );

  const handleConfirmDelete = async () => {
    await onDeleteSelected();
    setConfirmDeleteOpen(false);
  };

  return (
    <>
      <BackScreenDiv grayImage />
      
      <ScreenDiv column mobileJustifySpace fullWidth>
        <HeaderDiv />

        <ContentDiv
          gap={!isDesktop && "5px"}
          paddingBack={isDesktop}
          borderRadius="30px"
          alignStart
          justifyStart
          height="auto"
          heightDesktop="auto"
          style={{ minWidth: 0, width: "100%", maxWidth: "100%" }}
        >
          <Div row>
            {
              isDesktop && 
              <H1>Users</H1>
            }
            <Button onClick={onRefresh} disabled={loading || deleting} width="auto" greenButton>
              {loading ? "Loading…" : "Update"}
            </Button>
          </Div>
          
          
          <Div height="auto" alignStart style={{ minWidth: 0, width: "100%", maxWidth: "100%" }}>
            <Div height="auto" justifyStart alignStart style={{ marginBottom: 8 }}>
              <P fontSize="14px" width="auto" textAlign="left" style={{ margin: 0 }}>
                Users: {totalUsers}
              </P>
            </Div>

            <Div height="auto" row justifyStart>
              <SearchWrap role="search">
                <Input
                  as="input"
                  type="search"
                  placeholder="Search by email…"
                  value={search}
                  onChange={(e) => onSearchChange(e.target.value)}
                  onKeyDown={handleKeyDown}            
                  style={{ paddingRight: search ? 32 : undefined }}
                  aria-label="Search by email"
                />
                {search && (
                  <ClearBtn
                    type="button"
                    aria-label="Очистить поиск"
                    onClick={() => onSearchChange("")}
                    title="Очистить"
                  >
                    <Image src={closeButton} alt="" />
                  </ClearBtn>
                )}
              </SearchWrap>
              
            </Div>

            {selectedCount > 0 && (
              <Div
                row
                justifyStart
                height="auto"
                gap="12px"
                style={{ marginBottom: 12, flexWrap: "wrap" }}
              >
                <P fontSize="14px" textWidth="auto" textAlign="left" style={{ margin: 0 }}>
                  Selected: {selectedCount}
                </P>
                <Button
                  type="button"
                  deleteButton
                  width="auto"
                  disabled={deleting || loading}
                  onClick={() => setConfirmDeleteOpen(true)}
                >
                  {deleting ? "Deleting…" : `Delete selected (${selectedCount})`}
                </Button>
                <Button
                  type="button"
                  temporaryButton
                  fontWeight="400"
                  width="auto"
                  disabled={deleting}
                  onClick={onClearSelection}
                >
                  Clear
                </Button>
              </Div>
            )}

            {error && (
              <div role="alert" style={{ color: "#c62828", marginBottom: 8 }}>
                {error}
              </div>
            )}

            <UsersTableScroll>
            <UsersTableFrame>
              <UsersTableHeaderDiv>
                <UsersHeaderCell style={{ padding: "8px 4px", textAlign: "center" }}>
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    disabled={selectableVisibleIds.length === 0 || deleting}
                    onChange={onToggleSelectAllVisible}
                    aria-label="Select all visible users"
                  />
                </UsersHeaderCell>
                <UsersHeaderCell>Email</UsersHeaderCell>
                <UsersHeaderCell>Plan</UsersHeaderCell>
                <UsersHeaderCell>Source</UsersHeaderCell>
                <UsersHeaderCell>Country</UsersHeaderCell>
                <UsersHeaderCell title="Push notifications">Push</UsersHeaderCell>
                <UsersHeaderCell title="Times the paywall was opened">Paywall</UsersHeaderCell>
                <UsersHeaderCell>
                  <button
                    type="button"
                    onClick={() => onSort?.("createdAt")}
                    title="Sort by registration date"
                    style={headerSortButtonStyle}
                  >
                    Created{sortMark("createdAt", sortKey, sortDir)}
                  </button>
                </UsersHeaderCell>
                <UsersHeaderCell>Pets</UsersHeaderCell>
                <UsersHeaderCell title="App that last started a session">Last app</UsersHeaderCell>
                <UsersHeaderCell>
                  <button
                    type="button"
                    onClick={() => onSort?.("lastActivity")}
                    title="Sort by last activity"
                    style={headerSortButtonStyle}
                  >
                    Last Activity{sortMark("lastActivity", sortKey, sortDir)}
                  </button>
                </UsersHeaderCell>
                {USER_ACTIVITY_COLUMNS.map((col) => (
                  <UsersActivityHeaderCell key={col.key} title={col.title}>
                    {col.label}
                  </UsersActivityHeaderCell>
                ))}
                <UsersActivityHeaderCell title="Articles marked as read">Articles</UsersActivityHeaderCell>
              </UsersTableHeaderDiv>

              <UserTableBodyDiv>
                {rows.map((u) => {
                  const hasPets = u.pets && u.pets.length > 0;
                  const isSelf = u.id === currentUserId;
                  const isSelected = selectedIds.has(u.id);
                  const lastApp = getLastAppLabel(u);
                  const push = getPushLabel(u);
                  const paywall = getPaywallLabel(u);
                  const articlesRead = getArticlesReadCount(u);
                  const plan = isPremiumUser(u) ? getPlanDetails(u) : null;
                  return (
                    <UsersTableRowDiv key={u.id}>
                      <TableColumnDiv style={{ padding: "0 4px", textAlign: "center", wordBreak: "normal" }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={isSelf || deleting}
                          title={isSelf ? "Cannot delete your own account" : undefined}
                          onChange={() => onToggleSelect(u.id)}
                          aria-label={
                            isSelf
                              ? "Your account (cannot delete)"
                              : `Select ${u.email || u.id}`
                          }
                        />
                      </TableColumnDiv>
                      <TableColumnDiv
                        title={u.email || undefined}
                        style={{ whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden", wordBreak: "normal" }}
                      >
                        {u.email || "—"}
                        <FamilyLines user={u} />
                      </TableColumnDiv>
                      <TableColumnDiv
                        title={isPremiumUser(u) ? (u.premiumSource || "Premium") : "free"}
                        style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: 2 }}
                      >
                        <PlanMark user={u} />
                        {plan && (plan.label || plan.note) ? (
                          <span style={{ fontSize: 10, lineHeight: "12px", color: "#555", textAlign: "center" }}>
                            {plan.label}
                            {plan.note ? (
                              <span style={{ display: "block", color: "#00897b", fontWeight: 600 }}>
                                {plan.note}
                              </span>
                            ) : null}
                          </span>
                        ) : null}
                      </TableColumnDiv>
                      <TableColumnDiv title={u.signupPlatform || undefined}>
                        {getSignupSourceLabel(u)}
                      </TableColumnDiv>
                      <TableColumnDiv title={u.signupCountry || undefined}>
                        {getSignupCountryLabel(u)}
                      </TableColumnDiv>
                      <TableColumnDiv title={push.title}>
                        {push.label}
                      </TableColumnDiv>
                      <TableColumnDiv title={paywall.title} style={{ textAlign: "center" }}>
                        {paywall.count > 0 ? (
                          <span style={{ fontWeight: 600 }}>{paywall.count}</span>
                        ) : (
                          <span style={{ color: "#bbb" }}>—</span>
                        )}
                      </TableColumnDiv>
                      <TableColumnDiv>{formatDate(u.createdAt)}</TableColumnDiv>
                      <TableColumnDiv style={{ overflow: "visible", wordBreak: "normal", textAlign: "left", paddingLeft: 8 }}>
                        {hasPets ? (
                          <Div column gap="2px" justifyStart alignStart style={{ width: "100%" }}>
                            {u.pets.map((p) => (
                              <P
                                key={p.id}
                                fontSize="12px"
                                width="auto"
                                textAlign="left"
                                style={{ margin: 0, minWidth: 0 }}
                              >
                                {formatPetLabel(p)}
                              </P>
                            ))}
                          </Div>
                        ) : (
                          <P fontSize="12px" width="auto" textAlign="left" style={{ color: "#999", margin: 0 }}>No pets</P>
                        )}
                      </TableColumnDiv>
                      <TableColumnDiv title={lastApp.title}>
                        {lastApp.label}
                      </TableColumnDiv>
                      <TableColumnDiv>
                        {u.lastActivity ? formatDate(u.lastActivity) : "—"}
                      </TableColumnDiv>
                      {USER_ACTIVITY_COLUMNS.map((col) => {
                        const count = u.activity?.[col.key] || 0;
                        const isLatest = u.lastActivityColumn === col.key && count > 0;
                        const latestAt = isLatest ? formatDate(u.lastActivityColumnAt) : "";
                        const title = isLatest
                          ? `Latest action${latestAt && latestAt !== "—" ? `: ${latestAt}` : ""}`
                          : count
                            ? `${count} ${col.title.toLowerCase()}`
                            : col.title;
                        return (
                          <UsersActivityCell key={col.key} title={title}>
                            {count > 0 ? (
                              isLatest ? (
                                <UsersActivityLatest>{count}</UsersActivityLatest>
                              ) : (
                                <span style={{ fontWeight: 600 }}>{count}</span>
                              )
                            ) : (
                              <span style={{ color: "#bbb" }}>—</span>
                            )}
                          </UsersActivityCell>
                        );
                      })}
                      <UsersActivityCell
                        title={
                          articlesRead > 0
                            ? `${articlesRead} article${articlesRead === 1 ? "" : "s"} marked as read`
                            : "No articles marked as read"
                        }
                      >
                        {articlesRead > 0 ? (
                          <span style={{ fontWeight: 600 }}>{articlesRead}</span>
                        ) : (
                          <span style={{ color: "#bbb" }}>—</span>
                        )}
                      </UsersActivityCell>
                    </UsersTableRowDiv>
                  );
                })}
                {rows.length === 0 && (
                  <UserTableRowDiv>
                    <UserColumnRowDiv>No data yet</UserColumnRowDiv>
                  </UserTableRowDiv>
                )}
              </UserTableBodyDiv>
            </UsersTableFrame>
            </UsersTableScroll>

            {!done && (
              <button onClick={onLoadMore} disabled={loading || deleting} style={{ marginTop: 12, padding: "8px 12px" }}>
                {loading ? "Loading…" : "Load more"}
              </button>
            )}
          </Div>


          <Div height="auto" alignStart padding='0 0 30px 0'>
              <Button deleteButton width='auto' padding={isDesktop && '0'} onClick={openModal}>Logout</Button>
          </Div>
        </ContentDiv>
      </ScreenDiv>

      <CustomModal open={open} onClose={closeModal} title="Logout" details={true}>
          <Div gap="15px" row>
          <Button onClick={confirmLogout} greenButton fontSize="14px" width="auto">
              Выйти?
          </Button>
          </Div>
      </CustomModal>

      <CustomModal
        open={confirmDeleteOpen}
        onClose={() => !deleting && setConfirmDeleteOpen(false)}
        title="Delete selected users?"
      >
        <P style={{ marginBottom: 12 }}>
          This permanently deletes Firestore data, Storage files, and Auth accounts for{" "}
          {selectedCount} user{selectedCount === 1 ? "" : "s"}. This cannot be undone.
        </P>
        {selectedEmailsPreview.length > 0 && (
          <P style={{ marginBottom: 20, fontSize: 13, color: "#555" }}>
            {selectedEmailsPreview.join(", ")}
            {selectedCount > selectedEmailsPreview.length
              ? ` (+${selectedCount - selectedEmailsPreview.length} more)`
              : ""}
          </P>
        )}
        <Div row gap="10px" justifyEnd>
          <Button
            type="button"
            temporaryButton
            fontWeight="400"
            width="auto"
            disabled={deleting}
            onClick={() => setConfirmDeleteOpen(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            deleteButton
            width="auto"
            disabled={deleting}
            onClick={handleConfirmDelete}
          >
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </Div>
      </CustomModal>
      
    </>
  );
}
